import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import Button from '@renderer/components/Button'
import { useCountdown } from '@renderer/utils/countdownUtils'

import { FrameOption } from '@renderer/types/configState'

const EditFramePage: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { frames: allFrames, frameCategories, config: globalConfig } = useConfigStore()
  const config = globalConfig?.pages.frame
  const { setSessionState, logActivity } = useSessionStateStore()
  const [selectedCategory, setSelectedCategory] = useState('all')
  const isPotrait =
    (globalConfig?.screenSize.height || 1080) > (globalConfig?.screenSize.width || 1920)
  const [remainingTime, setRemainingTime] = useState<string>('--')
  const isCategoryModeEnabled = config?.settings?.categoryModeEnabled || false

  const [currentFrames, setCurrentFrames] = useState<FrameOption[]>([])
  const [selectedFrame, setSelectedFrame] = useState<string | null>(null)

  // Initialize countdown with the useCountdown hook
  const frameTimeout = config?.settings?.frameTimeout || 0
  const countdown = useCountdown(frameTimeout)

  useEffect(() => {
    let initialFramesToDisplay: FrameOption[] = []

    if (isCategoryModeEnabled && frameCategories.length > 0) {
      // Set initial selected category to the first one
      setSelectedCategory(frameCategories[0].name)
      initialFramesToDisplay = frameCategories[0].frames || []
    } else {
      // Fallback to 'all' if not in category mode or no categories
      setSelectedCategory('all')
      initialFramesToDisplay = allFrames
    }
    setCurrentFrames(initialFramesToDisplay)

    if (initialFramesToDisplay.length > 0) {
      setSelectedFrame(initialFramesToDisplay[0]._id)
    }
  }, [isCategoryModeEnabled, frameCategories, allFrames])

  useEffect(() => {
    if (isCategoryModeEnabled && selectedCategory !== 'all') {
      const category = frameCategories.find((cat) => cat.name === selectedCategory)
      setCurrentFrames(category?.frames || [])
    } else if (!isCategoryModeEnabled) {
      // In non-category mode or 'all' category selected, use all frames
      setCurrentFrames(allFrames)
    }
  }, [isCategoryModeEnabled, selectedCategory, frameCategories, allFrames])

  useEffect(() => {
    if (currentFrames.length > 0 && !selectedFrame) {
      setSelectedFrame(currentFrames[0]._id)
    } else if (currentFrames.length === 0 && selectedFrame) {
      setSelectedFrame(null)
    }
  }, [currentFrames, selectedFrame])

  const handleNext = (): void => {
    if (!selectedFrame) return

    const frame = allFrames.find((f) => f._id === selectedFrame)
    if (!frame) return

    // Find first photo type image and get its ratio
    const photoImage = frame.drawParams.images.find((img) => img.type.startsWith('photo-'))
    const photoRatio = photoImage ? photoImage.width / photoImage.height : null

    // FIXED: Only update specific userPreferences, preserve other session data
    setSessionState({
      userPreferences: {
        frameId: selectedFrame,
        framePhotoRatio: photoRatio
      }
    })

    const frameName = allFrames.find((f) => f._id === selectedFrame)?.name
    logActivity(`Selected frame for editing: ${frameName || selectedFrame}`, {
      frameId: selectedFrame,
      mode: 'edit'
    })

    // Navigate directly to select page for editing
    navigate(`/${projectId}/select`)
  }

  // Set up countdown callbacks with dependencies on selectedFrame
  useEffect(() => {
    if (frameTimeout > 0) {
      // Set the onTick callback to update the remaining time display
      countdown.setOnTick(() => {
        setRemainingTime(countdown.getFormattedRemainingSeconds())
      })

      // Set the onComplete callback that will use the latest state
      countdown.setOnComplete(() => {
        const frameName = allFrames.find((f) => f._id === selectedFrame)?.name
        logActivity(
          `Frame selection timed out for editing. Automatically selected frame: ${frameName || selectedFrame}`,
          {
            frameId: selectedFrame,
            timeoutDuration: frameTimeout,
            mode: 'edit'
          }
        )
        handleNext()
      })
    }
  }, [selectedFrame, frameTimeout, setSessionState])

  // Start the countdown on mount
  useEffect(() => {
    if (frameTimeout > 0) {
      countdown.start()

      return (): void => {
        countdown.stop()
      }
    }
    return
  }, [frameTimeout])

  return (
    <div className="w-full h-full flex relative" data-page="frame">
      {/* Frame Selection */}
      <div className="flex-1 flex flex-col w-full max-w-[70%] relative z-50">
        {/* Header for Edit Mode */}
        <div className="px-12 py-8 text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Choose Your Frame</h1>
          <p className="text-xl text-gray-600">Select a frame for your edited photo strip</p>
        </div>

        {/* Category Tabs - Only show if frameCategories exist and are not empty */}
        {isCategoryModeEnabled && frameCategories && frameCategories.length > 0 && (
          <div className="shadow-xl w-full relative z-50">
            <div className="overflow-x-auto w-full flex justify-left gap-4 px-12 py-4 z-50">
              {frameCategories.map((category) => (
                <button
                  type="button"
                  key={category.name}
                  onClick={() => setSelectedCategory(category.name)}
                  className={`px-8 py-3 rounded-full text-xl whitespace-nowrap transition-all
                    ${
                      selectedCategory === category.name
                        ? 'bg-veranda text-white'
                        : 'border border-carbon text-carbon'
                    }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Frames Grid */}
        <div
          className={`flex-1 overflow-y-auto px-16 mt-8 ${isCategoryModeEnabled && frameCategories?.length ? 'py-8' : 'py-4'} relative z-50`}
        >
          <div
            className={`grid ${isPotrait ? 'grid-cols-1' : 'grid-cols-3'} gap-12 items-end`}
            role="listbox"
            title="Frames"
          >
            {currentFrames.map((frame) => (
              <button
                role="option"
                aria-label={frame.name}
                type="button"
                key={frame._id}
                onClick={() => setSelectedFrame(frame._id)}
                className={`cursor-pointer transition-all flex flex-col p-8 items-center
                  ${selectedFrame === frame._id ? 'scale-105' : ''}`}
              >
                <img
                  crossOrigin="anonymous"
                  src={frame.thumbnail}
                  alt={frame.name}
                  className={`w-full shadow-xl object-contain rounded-lg ${
                    selectedFrame === frame._id
                      ? 'border-4 border-veranda'
                      : 'border border-gray-100'
                  }`}
                />
                <p className="mt-2 text-black text-xl">{frame.name}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Frame Preview */}
      <div
        className={`bg-whiteout shadow-xl flex flex-col items-center justify-center gap-12 w-full ${isPotrait ? 'p-12 max-w-[55%]' : 'p-8 max-w-[35%]'}`}
      >
        <div className="w-[426px] px-4">
          <img
            src={allFrames.find((f) => f._id === selectedFrame)?.thumbnail || ''}
            alt="Preview"
            crossOrigin="anonymous"
            className="w-full object-contain drop-shadow-xl"
          />
        </div>

        <div className="flex flex-col items-center gap-4">
          {(config?.settings?.frameTimeout || 0) > 0 && (
            <span className="text-2xl text-carbon">{remainingTime}</span>
          )}
          {/* Next Button */}
          <Button text="Next" onClick={handleNext} data-nav-to="select" />
        </div>
      </div>
    </div>
  )
}

export default EditFramePage
